import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Mail, Lock, User, AlertCircle, CheckCircle, Building2, Briefcase } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const RegisterForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userType, setUserType] = useState('candidate');
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    setIsLoading(true);
    // Remove username from data since it's auto-generated on backend
    const { username, ...userData } = data;
    const result = await registerUser({ ...userData, user_type: userType });
    
    if (result.success) {
      toast.success('Registration successful! Welcome!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };

  const passwordRequirements = [
    { text: 'At least 8 characters', met: password && password.length >= 8 },
    { text: 'Contains uppercase letter', met: password && /[A-Z]/.test(password) },
    { text: 'Contains lowercase letter', met: password && /[a-z]/.test(password) },
    { text: 'Contains number', met: password && /\d/.test(password) },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
            <User className="w-6 h-6 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{' '}
          <Link
            to="/login"
            className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
          >
            sign in to your existing account
          </Link>
        </p>
      </div>

      {/* User Type Selection */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-4 px-4 shadow sm:rounded-lg sm:px-6">
          <div className="text-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">I am a...</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setUserType('candidate')}
              className={`p-4 border-2 rounded-lg text-center transition-all duration-200 ${
                userType === 'candidate'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <Briefcase className="w-8 h-8 mx-auto mb-2" />
              <div className="font-medium">Candidate</div>
              <div className="text-sm text-gray-500">Looking for jobs</div>
            </button>
            <button
              type="button"
              onClick={() => setUserType('company')}
              className={`p-4 border-2 rounded-lg text-center transition-all duration-200 ${
                userType === 'company'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <Building2 className="w-8 h-8 mx-auto mb-2" />
              <div className="font-medium">Company</div>
              <div className="text-sm text-gray-500">Hiring talent</div>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                  First name
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('first_name', {
                      required: 'First name is required',
                      minLength: {
                        value: 2,
                        message: 'First name must be at least 2 characters',
                      },
                    })}
                    type="text"
                    autoComplete="given-name"
                    className={`input-field pl-10 ${
                      errors.first_name ? 'border-red-300 focus:ring-red-500' : ''
                    }`}
                    placeholder="John"
                  />
                </div>
                {errors.first_name && (
                  <div className="mt-1 flex items-center text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.first_name.message}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                  Last name
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('last_name', {
                      required: 'Last name is required',
                      minLength: {
                        value: 2,
                        message: 'Last name must be at least 2 characters',
                      },
                    })}
                    type="text"
                    autoComplete="family-name"
                    className={`input-field pl-10 ${
                      errors.last_name ? 'border-red-300 focus:ring-red-500' : ''
                    }`}
                    placeholder="Doe"
                  />
                </div>
                {errors.last_name && (
                  <div className="mt-1 flex items-center text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.last_name.message}
                  </div>
                )}
              </div>
            </div>


            {/* Company-specific fields */}
            {userType === 'company' && (
              <>
                <div>
                  <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                    Company Name *
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building2 className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register('company_name', {
                        required: userType === 'company' ? 'Company name is required' : false,
                      })}
                      type="text"
                      className={`input-field pl-10 ${
                        errors.company_name ? 'border-red-300 focus:ring-red-500' : ''
                      }`}
                      placeholder="Your Company Name"
                    />
                  </div>
                  {errors.company_name && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.company_name.message}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="company_website" className="block text-sm font-medium text-gray-700">
                    Company Website
                  </label>
                  <input
                    {...register('company_website', {
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: 'Please enter a valid URL (e.g., https://example.com)',
                      },
                    })}
                    type="url"
                    className={`input-field ${
                      errors.company_website ? 'border-red-300 focus:ring-red-500' : ''
                    }`}
                    placeholder="https://yourcompany.com"
                  />
                  {errors.company_website && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.company_website.message}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="company_size" className="block text-sm font-medium text-gray-700">
                    Company Size
                  </label>
                  <select
                    {...register('company_size')}
                    className="input-field"
                  >
                    <option value="">Select company size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="company_description" className="block text-sm font-medium text-gray-700">
                    Company Description
                  </label>
                  <textarea
                    {...register('company_description')}
                    rows={3}
                    className={`input-field ${
                      errors.company_description ? 'border-red-300 focus:ring-red-500' : ''
                    }`}
                    placeholder="Tell us about your company..."
                  />
                </div>
              </>
            )}

            {/* Candidate-specific fields */}
            {userType === 'candidate' && (
              <>
                <div>
                  <label htmlFor="job_title" className="block text-sm font-medium text-gray-700">
                    Current Job Title
                  </label>
                  <input
                    {...register('job_title')}
                    type="text"
                    className="input-field"
                    placeholder="e.g., Software Engineer"
                  />
                </div>

                <div>
                  <label htmlFor="current_company" className="block text-sm font-medium text-gray-700">
                    Current Company
                  </label>
                  <input
                    {...register('current_company')}
                    type="text"
                    className="input-field"
                    placeholder="e.g., Google"
                  />
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                    Location
                  </label>
                  <input
                    {...register('location')}
                    type="text"
                    className="input-field"
                    placeholder="e.g., San Francisco, CA"
                  />
                </div>

                <div>
                  <label htmlFor="linkedin_url" className="block text-sm font-medium text-gray-700">
                    LinkedIn Profile
                  </label>
                  <input
                    {...register('linkedin_url', {
                      pattern: {
                        value: /^https?:\/\/(www\.)?linkedin\.com\/in\/.+/,
                        message: 'Please enter a valid LinkedIn profile URL',
                      },
                    })}
                    type="url"
                    className={`input-field ${
                      errors.linkedin_url ? 'border-red-300 focus:ring-red-500' : ''
                    }`}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                  {errors.linkedin_url && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.linkedin_url.message}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="github_url" className="block text-sm font-medium text-gray-700">
                    GitHub Profile
                  </label>
                  <input
                    {...register('github_url', {
                      pattern: {
                        value: /^https?:\/\/(www\.)?github\.com\/.+/,
                        message: 'Please enter a valid GitHub profile URL',
                      },
                    })}
                    type="url"
                    className={`input-field ${
                      errors.github_url ? 'border-red-300 focus:ring-red-500' : ''
                    }`}
                    placeholder="https://github.com/yourusername"
                  />
                  {errors.github_url && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.github_url.message}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  type="email"
                  autoComplete="email"
                  className={`input-field pl-10 ${
                    errors.email ? 'border-red-300 focus:ring-red-500' : ''
                  }`}
                  placeholder="john@example.com"
                />
              </div>
              {errors.email && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.email.message}
                </div>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters',
                    },
                  })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`input-field pl-10 pr-10 ${
                    errors.password ? 'border-red-300 focus:ring-red-500' : ''
                  }`}
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  )}
                </button>
              </div>
              {errors.password && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.password.message}
                </div>
              )}

              {/* Password Requirements */}
              {password && (
                <div className="mt-2 space-y-1">
                  {passwordRequirements.map((req, index) => (
                    <div key={index} className="flex items-center text-sm">
                      {req.met ? (
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      ) : (
                        <div className="w-4 h-4 border border-gray-300 rounded-full mr-2" />
                      )}
                      <span className={req.met ? 'text-green-600' : 'text-gray-500'}>
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="password2" className="block text-sm font-medium text-gray-700">
                Confirm password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password2', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === password || 'Passwords do not match',
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`input-field pl-10 pr-10 ${
                    errors.password2 ? 'border-red-300 focus:ring-red-500' : ''
                  }`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  )}
                </button>
              </div>
              {errors.password2 && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.password2.message}
                </div>
              )}
            </div>

            {/* Terms and Conditions */}
            <div className="flex items-center">
              <input
                {...register('terms', {
                  required: 'You must accept the terms and conditions',
                })}
                id="terms"
                name="terms"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
                I agree to the{' '}
                <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                  Terms and Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                  Privacy Policy
                </Link>
              </label>
            </div>
            {errors.terms && (
              <div className="flex items-center text-sm text-red-600">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.terms.message}
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating account...
                  </div>
                ) : (
                  'Create account'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
